import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, UserRole } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle, Shield, Warehouse, Truck } from 'lucide-react';
import { BilingualText } from '../components/BilingualText';
export function Login() {
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>('STAFF');
  const [email, setEmail] = useState('staff@das.com');
  const [password, setPassword] = useState('password123');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    const emails: Record<UserRole, string> = {
      ADMIN: 'admin@das.com',
      STAFF: 'staff@das.com',
      SUPPLIER: 'supplier@global.com'
    };
    setEmail(emails[role]);
    setError('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // Simulate authentication logic
    setTimeout(() => {
      if (!email || !password) {
        setError('Please enter both email and password');
        setIsLoading(false);
        return;
      }
      login(selectedRole);
      navigate('/');
      setIsLoading(false);
    }, 800);
  };
  return <div className="min-h-screen bg-[#2E3192] flex items-center justify-center p-4" data-id="element-696">
      <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 bg-white rounded-3xl overflow-hidden shadow-2xl" data-id="element-697">
        {/* Left Side - Branding */}
        <div className="bg-slate-50 p-8 md:p-12 flex flex-col justify-between border-r border-slate-100" data-id="element-698">
          <div data-id="element-699">
            <div className="flex items-center gap-4 mb-8" data-id="element-700">
              <img src="https://cdn.magicpatterns.com/uploads/8gwbCWtCALwvXSSg9wjHzH/ChatGPT_Image_Jul_11,_2026,_08_45_11_PM.png" alt="DAS Logo" className="h-16 w-auto" data-id="element-701" />
              <div className="flex flex-col" data-id="element-702">
                <span className="text-4xl font-black text-[#2E3192] leading-none" data-id="element-703">
                  DAS
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1" data-id="element-704">
                  Dock Appointment Scheduling
                </span>
              </div>
            </div>
            <h1 className="text-3xl font-black text-slate-800 leading-tight mb-4" data-id="element-705">
              Delivery Appointment Scheduling System
            </h1>
            <p className="text-slate-500 leading-relaxed" data-id="element-706">
              Streamline your warehouse inbound operations with our mobile-first
              scheduling platform.
            </p>
          </div>
          <div className="hidden md:block" data-id="element-707">
            <div className="flex -space-x-3 mb-4" data-id="element-708">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-200" data-id="element-709" />)}
            </div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-710">
              Trusted by 500+ Logistics Partners
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-711">
          <div className="space-y-2 mb-6" data-id="element-712">
            <BilingualText en="Welcome Back" ar="مرحباً بك مجدداً" className="text-2xl font-bold text-slate-800" data-id="element-713" />
            <p className="text-slate-500 text-sm" data-id="element-714">
              Select your portal and sign in
            </p>
          </div>

          {/* Role Selection Icons */}
          <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-715">
            <RoleTab icon={Shield} label="Admin" active={selectedRole === 'ADMIN'} onClick={() => handleRoleSelect('ADMIN')} color="text-purple-600" bgColor="bg-purple-50" data-id="element-716" />
            <RoleTab icon={Warehouse} label="Staff" active={selectedRole === 'STAFF'} onClick={() => handleRoleSelect('STAFF')} color="text-blue-600" bgColor="bg-blue-50" data-id="element-717" />
            <RoleTab icon={Truck} label="Supplier" active={selectedRole === 'SUPPLIER'} onClick={() => handleRoleSelect('SUPPLIER')} color="text-green-600" bgColor="bg-green-50" data-id="element-718" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-719">
            {error && <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-720">
                <AlertCircle size={18} data-id="element-721" />
                <span data-id="element-722">{error}</span>
              </div>}

            <div className="space-y-2" data-id="element-723">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1" data-id="element-724">
                Email Address
              </label>
              <div className="relative group" data-id="element-725">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2E3192] transition-colors" size={20} data-id="element-726" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-[#2E3192]/20 focus:border-[#2E3192] transition-all" data-id="element-727" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-728">
              <div className="flex justify-between items-center px-1" data-id="element-729">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-730">
                  Password
                </label>
                <button type="button" className="text-[10px] font-bold text-[#2E3192] hover:underline" data-id="element-731">
                  Forgot Password?
                </button>
              </div>
              <div className="relative group" data-id="element-732">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#2E3192] transition-colors" size={20} data-id="element-733" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-[#2E3192]/20 focus:border-[#2E3192] transition-all" data-id="element-734" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-735">
                  {showPassword ? <EyeOff size={20} data-id="element-736" /> : <Eye size={20} data-id="element-737" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="w-full bg-[#2E3192] text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-[#1e2170] transition-all shadow-lg shadow-blue-900/20 disabled:opacity-70 disabled:cursor-not-allowed group" data-id="element-738">
              {isLoading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-739" /> : <>
                  <span data-id="element-740">
                    Sign In as{' '}
                    {selectedRole.charAt(0) + selectedRole.slice(1).toLowerCase()}
                  </span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-741" />
                </>}
            </button>
          </form>
        </div>
      </div>
    </div>;
}
function RoleTab({
  icon: Icon,
  label,
  active,
  onClick,
  color,
  bgColor
}: any) {
  return <button onClick={onClick} className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${active ? `border-[#2E3192] ${bgColor} shadow-sm` : 'border-slate-100 hover:border-slate-200 bg-white'}`} data-id="element-742">
      <div className={`p-2 rounded-xl ${active ? color : 'text-slate-400'}`} data-id="element-743">
        <Icon size={24} data-id="element-744" />
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-[#2E3192]' : 'text-slate-400'}`} data-id="element-745">
        {label}
      </span>
    </button>;
}